import React, { memo, useContext, useEffect, useState } from 'react';
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from 'reactflow';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { PRIMITIVE_COLORS, type PrimitiveType } from './workflowGraphTypes';

export interface PrimitiveNodeData {
  /** Absent only for an unresolved tier-3 action reference loaded from a draft (ref: 06_...md §5). */
  primType?: PrimitiveType;
  name: string;
  config: Record<string, unknown>;
  /** NEW — set when this node was authored via an Action Catalog entry (ref: 06_...md §3). */
  actionId?: string;
  /** NEW — friendly palette label for the action, shown instead of the raw primitive label. */
  actionLabel?: string;
  /** NEW — friendly field values for the action's `definition.inputs`. */
  inputs?: Record<string, unknown>;
  /** NEW — server-computed, read-only header text (from GraphNode.display_label via
   * docToRfNodes). Always a good human-readable string when present; falls back to `name`
   * for client-created nodes that haven't round-tripped through the server yet. */
  displayLabel?: string;
  assumed?: boolean;
  /** true when a live test run is currently parked on this node (ref: spec §5.5) */
  running?: boolean;
  /** NEW — display-only; the Action Catalog's connection_id for this node (set in
   * buildActionNode). Never sent to the backend — rfToDoc whitelists its own fields and
   * doesn't read this. Used only to look up a real app icon below. */
  connectionId?: string;
  /** NEW — display-only; resolved from connectionId against the Apps catalog in
   * WorkflowCanvasTab's displayNodes memo. Renders in place of the generic primitive icon
   * when present, so a CALL_API node shows the actual app (Salesforce, Jira, ...) it targets. */
  appIconUrl?: string;
  /** NEW — display-only; computed in WorkflowCanvasTab's displayNodes memo from the full edges
   * list. True when this node has no outgoing connection yet, so the dangling "+" button below
   * knows whether to show (only terminal/unconnected nodes get one; END never does). */
  hasOutgoingEdge?: boolean;
  /** NEW — opens the node picker to add and auto-connect a new node right after this one. */
  onAddAfter?: () => void;
}

/**
 * NEW — Auto-layout's direction (WorkflowCanvasTab.tsx). Connector handles must match:
 * vertical layout needs top/bottom handles, horizontal needs left/right — otherwise edges have
 * to loop around node bodies to reach a handle on the "wrong" side, which is exactly the bug a
 * prior fix (see computeAutoLayout's comment) avoided by keeping layout and handles in sync.
 * Defaults to 'vertical' so a node rendered without this provider looks exactly as before.
 */
export type LayoutDirection = 'vertical' | 'horizontal';
export const LayoutDirectionContext = React.createContext<LayoutDirection>('vertical');

/** n8n-style icon per primitive type — purely visual, has no bearing on the saved graph payload. */
const PRIMITIVE_ICONS: Record<PrimitiveType, React.ElementType> = {
  START: PlayArrowIcon,
  END: StopIcon,
  CALL_API: PublicIcon,
  CLASSIFY: CategoryIcon,
  NOTIFY: MailOutlineIcon,
  WAIT_SIGNAL: HourglassEmptyIcon,
  BRANCH: CallSplitIcon,
  AGENT: SmartToyIcon,
};

/**
 * NEW — static icon image override per primitive type, same asset already used elsewhere in the
 * app (e.g. the Odoo fallback icon in AppsGrid.tsx uses this identical /images/apps/... pattern).
 * Falls back to the plain MUI icon above (PRIMITIVE_ICONS) if the image ever fails to load.
 */
const PRIMITIVE_ICON_IMAGES: Partial<Record<PrimitiveType, string>> = {
  NOTIFY: '/images/apps/gmail.png',
};

/**
 * Single custom node renderer for all 7 primitive types, styled as an n8n-style icon card:
 * a small icon square with the name/type label underneath it. Handle positions follow
 * LayoutDirectionContext — top/bottom for the original vertical Auto-layout, left/right when
 * Auto-layout's alternate horizontal mode is active — so edges never have to loop around a
 * node body to reach a handle on the "wrong" side (see computeAutoLayout in WorkflowCanvasTab.tsx).
 */
function PrimitiveNode({ id, data, selected }: NodeProps<PrimitiveNodeData>) {
  const muiTheme = useMuiTheme();
  const layoutDirection = useContext(LayoutDirectionContext);
  const [appIconFailed, setAppIconFailed] = useState(false);
  const [staticIconFailed, setStaticIconFailed] = useState(false);
  // NEW — React Flow caches each node's handle bounding boxes internally to compute connected
  // edges' paths, and does NOT automatically recompute that cache just because a handle's own
  // CSS position (or, as here, which container it's nested in) changes on re-render. Without
  // this, the small handle dots move to the correct new spot but edges keep drawing toward the
  // OLD cached coordinates — which is exactly what kept producing a bent/curved line even after
  // the dots themselves lined up correctly. updateNodeInternals is React Flow's documented fix
  // for "a node's handle configuration changed dynamically."
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [layoutDirection, id, updateNodeInternals]);
  const color = data.primType ? PRIMITIVE_COLORS[data.primType] : '#757575';
  // Header: server's display_label when present (always good/non-empty), else the node's own
  // name — covers client-created nodes (palette/manual add) that haven't hit the server yet.
  // No raw engine primitive type (START/CALL_API/NOTIFY/...) is ever shown on the card — only
  // this API-provided, human-readable text.
  const headerText = data.displayLabel ?? data.name;
  const Icon = (data.primType && PRIMITIVE_ICONS[data.primType]) || HelpOutlineIcon;
  const showAppIcon = Boolean(data.appIconUrl) && !appIconFailed;
  // Static per-type image (e.g. the Gmail icon for NOTIFY) — only used when no more-specific
  // resolved app icon (showAppIcon) applies, and falls back to the plain MUI icon on load failure.
  const staticIconImage = data.primType ? PRIMITIVE_ICON_IMAGES[data.primType] : undefined;
  const showStaticIcon = !showAppIcon && Boolean(staticIconImage) && !staticIconFailed;

  const isHorizontal = layoutDirection === 'horizontal';
  // NEW — bigger, more visible connector dots. ReactFlow's default handle is a small 6px circle;
  // this doubles it and adds a white ring so it stands out against both the icon square and the
  // canvas dot-grid background, matching the reference flow's chunkier connector style.
  const HANDLE_SIZE = 12;
  const handleSizeStyle = {
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    borderRadius: '50%',
    border: '2px solid #fff',
  } as const;

  return (
    <Box
      className="primitive-node-root"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120 }}
    >
      {/* Vertical mode (unchanged, original behavior): target handle anchored to the whole
          card's top edge, exactly as it always has been. */}
      {!isHorizontal && data.primType !== 'START' && (
        <Handle type="target" position={Position.Top} style={{ background: color, ...handleSizeStyle }} />
      )}

      <Box sx={{ position: 'relative' }}>
        {/* Horizontal mode: target handle is now a child of THIS icon-sized box (56x56) instead
            of the outer card (icon + label stacked below it) — top: '50%' + translateY(-50%) is
            a CSS-native center, always correct regardless of label text length, replacing an
            earlier hardcoded "top: 28" guess that didn't reliably line up with the next node's
            handle, producing a visibly bent connecting line instead of a level one. */}
        {isHorizontal && data.primType !== 'START' && (
          <Handle
            type="target"
            position={Position.Left}
            style={{ background: color, top: '50%', transform: 'translateY(-50%)', ...handleSizeStyle }}
          />
        )}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            border: `2px solid ${selected ? muiTheme.palette.primary.main : color}`,
            backgroundColor: muiTheme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: data.running
              ? '0 0 0 3px #f9a825aa'
              : selected
              ? `0 0 0 2px ${muiTheme.palette.primary.main}33`
              : 1,
          }}
        >
          {showAppIcon ? (
            <Box
              component="img"
              src={data.appIconUrl}
              alt=""
              onError={() => setAppIconFailed(true)}
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          ) : showStaticIcon ? (
            <Box
              component="img"
              src={staticIconImage}
              alt=""
              onError={() => setStaticIconFailed(true)}
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          ) : (
            <Icon sx={{ fontSize: 28, color }} />
          )}
        </Box>

        {/* Horizontal mode: source handle, same icon-centered approach as the target handle
            above — see that comment for why this replaced the earlier hardcoded offset. */}
        {isHorizontal && data.primType !== 'END' && (
          <Handle
            type="source"
            position={Position.Right}
            style={{ background: color, top: '50%', transform: 'translateY(-50%)', ...handleSizeStyle }}
          />
        )}

        {/* NEW — dangling-node quick-add: only shown for a node with no outgoing connection
            yet (and never on END, which is terminal by design). Anchored at the icon box's
            bottom-right corner in both layouts — offset from the source handle regardless of
            which side that handle is on (right-center in horizontal, bottom-center in vertical),
            so the two never overlap. */}
        {!data.hasOutgoingEdge && data.primType !== 'END' && data.onAddAfter && (
          <Tooltip title="Add next step">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                data.onAddAfter?.();
              }}
              sx={{
                position: 'absolute',
                top: 56,
                left: '100%',
                ml: 1,
                width: 22,
                height: 22,
                backgroundColor: 'primary.main',
                color: '#fff',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{ fontWeight: 600, textAlign: 'center', wordBreak: 'break-word', mt: 0.75, lineHeight: 1.2 }}
      >
        {headerText}
      </Typography>

      {/* Vertical mode (unchanged, original behavior): source handle anchored to the whole
          card's bottom edge, exactly as it always has been. */}
      {!isHorizontal && data.primType !== 'END' && (
        <Handle type="source" position={Position.Bottom} style={{ background: color, ...handleSizeStyle }} />
      )}
    </Box>
  );
}

export default memo(PrimitiveNode);
